import { DateTime } from 'luxon';
import { BROWSER_TZ } from './constants';

export const formatDate = (orderDate: string): string => {
	const dt = DateTime.fromISO(orderDate, { zone: BROWSER_TZ });
	const sameYear = dt.year === DateTime.now().year;
	return dt.toFormat(sameYear ? 'EEE, MMM d, h:mm a' : 'EEE, MMM d, yyyy, h:mm a');
};

/**
 * Status → tag background color for the Transactions table.
 *
 * Returns a literal hex string (not a CSS variable reference) because
 * `PunchupTag` resolves its background via CSS `color-mix()`, which requires
 * a concrete colour at parse time. Hex values mirror the `--punchup-color-*`
 * tokens — when a token value changes, update this mapping.
 *
 * Colour taxonomy (see `src/styles/CLAUDE.md` → "Status colour taxonomy"):
 * - Completed / Registered / Sold → lime (success): finalized happy path
 *   — money is in, tickets are issued.
 * - Pending → blue (info): in-flight, awaiting confirmation. Distinct from
 *   success because the outcome is not yet decided.
 * - Voided → yellow (warning): cancelled before completion. Quieter than red
 *   because no money was lost — the transaction simply did not finalize.
 * - Refunded → red (destructive): money was returned — the sale is reversed.
 * - Rejected → red (destructive): outright failure.
 * - default → grey (inactive): unknown / fallback state.
 *
 * Punchup orange is intentionally absent here — the primary brand colour is
 * reserved for active interaction signals (hover, focus, primary CTA), not
 * for static status indicators in dense tables.
 */
export const getStatusTagBackground = (status: string): string => {
	switch (status) {
		case 'Completed':
		case 'Registered':
		case 'Sold':
			return '#7CC11F'; // --punchup-color-status-success (lime)
		case 'Pending':
			return '#3b82f6'; // --punchup-color-status-info (blue)
		case 'Voided':
			return '#FACC15'; // --punchup-color-status-warning (yellow)
		case 'Refunded':
		case 'Rejected':
			return '#ef4444'; // --punchup-color-danger (red)
		default:
			return '#787B7E'; // --punchup-color-text-secondary (grey)
	}
};
